import api, { openSafeInvoice, openSafeTelegramLink } from '../api/client';
import { isMobileClient } from './telegram';

/** sku → invoice_link */
const INVOICE_CACHE_REV = '20260727k';
const cache = new Map();
/** sku → already sent invoice to bot chat this session */
const chatSent = new Set();
const inflight = new Map();

const revSku = (sku) => `${INVOICE_CACHE_REV}:${sku}`;

export function getCachedInvoice(sku) {
  return cache.get(revSku(sku)) || null;
}

function rememberLink(sku, link) {
  if (sku && link) cache.set(revSku(sku), link);
  return link || null;
}

export function prefetchStarsInvoice(sku, { deliverChat = false } = {}) {
  if (!sku) return Promise.resolve(null);
  const keySku = revSku(sku);
  if (!deliverChat && cache.has(keySku)) {
    return Promise.resolve({ link: cache.get(keySku), chat_sent: chatSent.has(sku) });
  }
  if (deliverChat && chatSent.has(sku) && cache.has(keySku)) {
    return Promise.resolve({ link: cache.get(keySku), chat_sent: true });
  }

  const key = `${sku}:${deliverChat ? 'chat' : 'link'}`;
  if (inflight.has(key)) return inflight.get(key);

  const p = api
    .createInvoice(sku, { deliver_chat: deliverChat })
    .then((res) => {
      const link = rememberLink(sku, res?.invoice_link);
      if (res?.chat_sent) chatSent.add(sku);
      return { link, chat_sent: Boolean(res?.chat_sent) || chatSent.has(sku), raw: res };
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, p);
  return p;
}

/** After chat/link payment, poll until UI can refresh (no openInvoice callback). */
export function watchPaymentSettle(onTick, { timeoutMs = 45000, intervalMs = 2500 } = {}) {
  if (typeof onTick !== 'function') return () => {};
  let stopped = false;
  let timer = null;
  const tick = async () => {
    if (stopped) return;
    try {
      await onTick();
    } catch (_) {
      /* ignore */
    }
  };
  const onVis = () => {
    if (document.visibilityState === 'visible') tick();
  };
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('focus', onVis);
  timer = setInterval(tick, intervalMs);
  // First check shortly after returning from pay sheet / bot
  setTimeout(tick, 1200);
  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('focus', onVis);
  };
  setTimeout(stop, timeoutMs);
  return stop;
}

function openedResult(via, { chatSentFlag = false } = {}) {
  if (via === 'invoice') {
    return {
      ok: true,
      reason: 'opened',
      via: 'invoice',
      chat_sent: chatSentFlag,
      needsRetap: false,
      // Native invoice callback usually fires; still poll if we also DM'd the bill
      needsPoll: Boolean(chatSentFlag),
    };
  }
  if (via === 'link') {
    return {
      ok: true,
      reason: 'opened_link',
      via: 'link',
      chat_sent: chatSentFlag,
      needsRetap: false,
      needsPoll: true,
    };
  }
  return null;
}

/**
 * Pay with Stars — single open path (callers must NOT also openSafeInvoice).
 */
export async function payWithStars({ sku, onStatus }) {
  try {
    window.Telegram?.WebApp?.ready?.();
  } catch (_) {
    /* ignore */
  }

  const mobile = isMobileClient();
  const cached = cache.get(revSku(sku));

  if (cached) {
    const via = openSafeInvoice(cached, onStatus);
    const hit = openedResult(via);
    if (hit) return hit;
    if (mobile && !chatSent.has(sku)) {
      try {
        const res = await prefetchStarsInvoice(sku, { deliverChat: true });
        if (res?.chat_sent) {
          return { ok: true, reason: 'chat_sent', chat_sent: true, needsRetap: false, needsPoll: true };
        }
      } catch (err) {
        return { ok: false, reason: 'error', error: err, needsRetap: false };
      }
    }
    try {
      if (openSafeTelegramLink(cached)) {
        return { ok: true, reason: 'telegram_link', needsRetap: false, needsPoll: true };
      }
    } catch (_) {
      /* ignore */
    }
    if (chatSent.has(sku)) {
      return { ok: true, reason: 'chat_sent', chat_sent: true, needsRetap: false, needsPoll: true };
    }
    return { ok: false, reason: 'needs_retap', needsRetap: true, needsPoll: false };
  }

  let pack;
  try {
    pack = await prefetchStarsInvoice(sku, { deliverChat: mobile && !chatSent.has(sku) });
  } catch (err) {
    return { ok: false, reason: 'error', error: err, needsRetap: false };
  }

  const link = pack?.link || cache.get(sku);
  if (!link) return { ok: false, reason: 'no_link', needsRetap: false };

  const via = openSafeInvoice(link, onStatus);
  const hit = openedResult(via, { chatSentFlag: Boolean(pack?.chat_sent) });
  if (hit) return hit;
  if (pack?.chat_sent) {
    return { ok: true, reason: 'chat_sent', chat_sent: true, needsRetap: false, needsPoll: true };
  }

  try {
    if (openSafeTelegramLink(link)) {
      return { ok: true, reason: 'telegram_link', needsRetap: false, needsPoll: true };
    }
  } catch (_) {
    /* ignore */
  }

  if (chatSent.has(sku)) {
    return { ok: true, reason: 'chat_sent', chat_sent: true, needsRetap: false, needsPoll: true };
  }

  return { ok: false, reason: 'needs_retap', needsRetap: true, needsPoll: false };
}
